import {
  createMockNotificationPreferences,
  type NotificationPreferences,
} from '@indutrust/notification-model';

const NOTIFICATION_PREFERENCES_SEED_KEY =
  '__idt_notificationPreferencesSeed__' as const;

type NotificationPreferencesSeedGlobals = typeof globalThis & {
  [NOTIFICATION_PREFERENCES_SEED_KEY]?: NotificationPreferences;
};

const seedGlobals = globalThis as NotificationPreferencesSeedGlobals;

/**
 * Mutable MSW seed for `${baseUrl}/notification_preferences`.
 *
 * Anchored on `globalThis` so Vite/browser bundles that import
 * `@indutrust/notification-data` share one singleton.
 */
let notificationPreferencesSeed: NotificationPreferences =
  seedGlobals[NOTIFICATION_PREFERENCES_SEED_KEY] ??
  createMockNotificationPreferences();

seedGlobals[NOTIFICATION_PREFERENCES_SEED_KEY] = notificationPreferencesSeed;

/** Current preferences seed (mutated by PATCH handlers). */
export function getNotificationPreferencesSeed(): NotificationPreferences {
  return notificationPreferencesSeed;
}

/** Replaces the preferences seed (used by unit specs). */
export function setNotificationPreferencesSeed(
  prefs: NotificationPreferences
): void {
  notificationPreferencesSeed = prefs;
  seedGlobals[NOTIFICATION_PREFERENCES_SEED_KEY] = prefs;
}

/** Resets the seed to the canonical mock defaults. */
export function resetNotificationPreferencesSeed(): void {
  setNotificationPreferencesSeed(createMockNotificationPreferences());
}

/** Merges a partial update into the seed and returns the result. */
export function patchNotificationPreferencesSeed(
  partial: Partial<NotificationPreferences>
): NotificationPreferences {
  const next: NotificationPreferences = {
    ...notificationPreferencesSeed,
    ...partial,
    eventTypes: {
      ...notificationPreferencesSeed.eventTypes,
      ...partial.eventTypes,
    },
  };
  setNotificationPreferencesSeed(next);
  return next;
}
